import React, { useState } from 'react';
import { SectionContainer, SubSection, PropsTable } from '../../index';
import { 
  Link, 
  PrimaryLink, 
  SecondaryLink, 
  TextLink, 
  ButtonLink, 
  NavLink, 
  BreadcrumbLink, 
  SocialLink 
} from '../Link';

export const LinkSection: React.FC = () => {
  const [activeNavItem, setActiveNavItem] = useState('home');

  const handleNavClick = (href: string) => {
    setActiveNavItem(href.substring(1));
  };

  const linkProps = [
    { name: 'children', type: 'ReactNode', default: 'required', description: 'Link content' },
    { name: 'href', type: 'string', default: 'required', description: 'Link destination URL' },
    { name: 'className', type: 'string', default: "''", description: 'Additional CSS classes' },
  ];

  const specializedLinkProps = [
    { name: 'Link', type: 'Component', default: 'Base', description: 'Base link component with href and children' },
    { name: 'PrimaryLink', type: 'Component', default: 'Primary', description: 'Primary styled link variant' },
    { name: 'SecondaryLink', type: 'Component', default: 'Secondary', description: 'Secondary styled link variant' },
    { name: 'TextLink', type: 'Component', default: 'Text', description: 'Text-only link variant' },
    { name: 'ButtonLink', type: 'Component', default: 'Button', description: 'Button-styled link variant' },
    { name: 'NavLink', type: 'Component', default: 'Nav', description: 'Navigation link variant' },
    { name: 'BreadcrumbLink', type: 'Component', default: 'Breadcrumb', description: 'Breadcrumb navigation link' },
    { name: 'SocialLink', type: 'Component', default: 'Social', description: 'Social media link variant' },
  ];

  return (
    <SectionContainer id="links">
      <h2 className="section-title">Link Components</h2>
      <p className="section-subtitle">Comprehensive link components for navigation, actions, and external references.</p>
      
      <SubSection title="Basic Link Variants">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 items-center">
            <Link href="/docs">Default Link</Link>
            <PrimaryLink href="/docs">Primary Link</PrimaryLink>
            <SecondaryLink href="/docs">Secondary Link</SecondaryLink>
            <TextLink href="/docs">Text Link</TextLink>
            <ButtonLink href="/docs">Button Link</ButtonLink>
          </div>
        </div>
      </SubSection>

      <SubSection title="Link Sizes">
        <div className="flex flex-wrap gap-4 items-center">
          <Link href="/docs" className="text-xs">Extra Small</Link>
          <Link href="/docs" className="text-sm">Small</Link>
          <Link href="/docs" className="text-base">Medium</Link>
          <Link href="/docs" className="text-lg">Large</Link>
          <Link href="/docs" className="text-xl">Extra Large</Link>
        </div>
      </SubSection>

      <SubSection title="Button Link Variants">
        <div className="flex flex-wrap gap-4 items-center">
          <ButtonLink href="/docs" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">Default Button</ButtonLink>
          <ButtonLink href="/docs" className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700">Primary Button</ButtonLink>
          <ButtonLink href="/docs" className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">Secondary Button</ButtonLink>
          <ButtonLink href="/docs" className="bg-transparent text-blue-600 border border-blue-600 px-4 py-2 rounded hover:bg-blue-50">Text Button</ButtonLink>
        </div>
      </SubSection>

      <SubSection title="Navigation Links">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 items-center">
            <NavLink href="/home" className="px-3 py-2 rounded transition-colors">
              Home
            </NavLink>
            <NavLink href="/docs" className="px-3 py-2 rounded transition-colors">
              Documentation
            </NavLink>
            <NavLink href="/about" className="px-3 py-2 rounded transition-colors">
              About
            </NavLink>
          </div>
          
          <div className="flex flex-wrap gap-4 items-center">
            <NavLink href="/docs" className="px-3 py-2 rounded bg-orange-100 text-orange-800">Active Nav Link</NavLink>
          </div>
        </div>
      </SubSection>

      <SubSection title="Breadcrumb Links">
        <div className="flex flex-wrap gap-2 items-center text-sm">
          <BreadcrumbLink href="/">Home</BreadcrumbLink>
          <span className="text-gray-400">/</span>
          <BreadcrumbLink href="/docs">Documentation</BreadcrumbLink>
          <span className="text-gray-400">/</span>
          <BreadcrumbLink href="/docs/components" className="text-gray-500 cursor-default">Components</BreadcrumbLink>
        </div>
      </SubSection>

      <SubSection title="Social Media Links">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 items-center">
            <SocialLink href="https://twitter.com/acutrack" className="flex items-center gap-2">
              Twitter
            </SocialLink>
            <SocialLink href="https://facebook.com/acutrack" className="flex items-center gap-2">
              Facebook
            </SocialLink>
            <SocialLink href="https://linkedin.com/company/acutrack" className="flex items-center gap-2">
              LinkedIn
            </SocialLink>
            <SocialLink href="https://github.com/acutrack" className="flex items-center gap-2">
              GitHub
            </SocialLink>
          </div>
          
          <div className="flex flex-wrap gap-4 items-center">
            <SocialLink href="https://twitter.com/acutrack" className="p-2 rounded-full hover:bg-gray-100">Twitter</SocialLink>
            <SocialLink href="https://facebook.com/acutrack" className="p-2 rounded-full hover:bg-gray-100">Facebook</SocialLink>
            <SocialLink href="https://linkedin.com/company/acutrack" className="p-2 rounded-full hover:bg-gray-100">LinkedIn</SocialLink>
            <SocialLink href="https://github.com/acutrack" className="p-2 rounded-full hover:bg-gray-100">GitHub</SocialLink>
            <SocialLink href="https://instagram.com/acutrack" className="p-2 rounded-full hover:bg-gray-100">Instagram</SocialLink>
            <SocialLink href="https://youtube.com/acutrack" className="p-2 rounded-full hover:bg-gray-100">YouTube</SocialLink>
          </div>
        </div>
      </SubSection>

      <SubSection title="Link with Icons">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 items-center">
            <Link href="/download" className="flex items-center gap-2">
              <span className="text-lg">⬇️</span>
              Download
            </Link>
          </div>
          
          <div className="flex flex-wrap gap-4 items-center">
            <Link href="https://example.com" className="flex items-center gap-2">
              External Link
              <span className="text-lg">🔗</span>
            </Link>
          </div>
          
          <div className="flex flex-wrap gap-4 items-center">
            <ButtonLink href="/submit" className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 flex items-center gap-2">
              <span className="text-lg">📝</span>
              Submit
              <span className="text-lg">➡️</span>
            </ButtonLink>
          </div>
        </div>
      </SubSection>

      <SubSection title="External Links">
        <div className="flex flex-wrap gap-4 items-center">
          <Link href="https://example.com" className="flex items-center gap-2">
            Example.com
          </Link>
          <Link href="https://github.com" className="flex items-center gap-2">
            GitHub
          </Link>
          <Link href="//cdn.example.com" className="flex items-center gap-2">
            CDN Link
          </Link>
        </div>
      </SubSection>

      <SubSection title="Disabled States">
        <div className="flex flex-wrap gap-4 items-center">
          <Link href="/docs" className="opacity-50 cursor-not-allowed">Disabled Link</Link>
          <ButtonLink href="/submit" className="bg-orange-600 text-white px-4 py-2 rounded opacity-50 cursor-not-allowed">Disabled Button</ButtonLink>
          <NavLink href="/admin" className="px-3 py-2 rounded opacity-50 cursor-not-allowed">Disabled Nav</NavLink>
        </div>
      </SubSection>

      <SubSection title="Custom Styling">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 items-center">
            <Link href="/docs" className="underline">With Underline</Link>
            <Link href="/docs" className="no-underline">Without Underline</Link>
            <Link href="/docs" className="font-bold">Bold Link</Link>
          </div>
          
          <div className="flex flex-wrap gap-4 items-center">
            <Link href="/docs" className="bg-blue-100 text-blue-800 px-3 py-2 rounded hover:bg-blue-200">Custom Background</Link>
            <Link href="/docs" className="border border-gray-300 px-3 py-2 rounded hover:bg-gray-50">Bordered Link</Link>
            <Link href="/docs" className="shadow-md px-3 py-2 rounded hover:shadow-lg">Shadowed Link</Link>
          </div>
        </div>
      </SubSection>

      <SubSection title="Interactive Examples">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 items-center">
            <Link href="/docs" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors">
              Hover Effect
            </Link>
            <Link href="/docs" className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 focus:ring-4 focus:ring-green-300 transition-all">
              Focus Ring
            </Link>
            <Link href="/docs" className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 active:scale-95 transition-transform">
              Active Scale
            </Link>
          </div>
          
          <div className="flex flex-wrap gap-4 items-center">
            <Link href="/docs" className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded hover:from-blue-600 hover:to-purple-700 transition-all">
              Gradient Background
            </Link>
            <Link href="/docs" className="border-2 border-blue-500 text-blue-600 px-4 py-2 rounded hover:bg-blue-500 hover:text-white transition-colors">
              Border Transform
            </Link>
          </div>
        </div>
      </SubSection>

      <SubSection title="Accessibility Features">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 items-center">
            <Link href="/docs" className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
              Focus Visible
            </Link>
            <Link href="/docs" className="sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 focus:z-50 focus:p-4 focus:bg-white focus:text-black">
              Skip to Content
            </Link>
          </div>
          
          <div className="text-sm text-gray-600">
            <p>• All links have proper focus indicators</p>
            <p>• External links are clearly marked</p>
            <p>• Disabled states are properly communicated</p>
            <p>• Screen reader friendly labels and descriptions</p>
          </div>
        </div>
      </SubSection>

      <SubSection title="Component Props">
        <div className="component-preview !p-0 !bg-transparent !border-none">
          <PropsTable data={linkProps} />
        </div>
        
        <div className="component-preview !p-0 !bg-transparent !border-none mt-8">
          <PropsTable data={specializedLinkProps} />
        </div>
      </SubSection>

      <SubSection title="Usage Examples">
        <div className="space-y-6">
          <div>
            <h4 className="font-semibold mb-2">Basic Usage</h4>
            <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto">
{`import { Link, PrimaryLink, ButtonLink } from '@acutrack-bookprint/acutrack-ds';

// Basic link
<Link href="/docs">Documentation</Link>

// Primary link
<PrimaryLink href="/get-started">Get Started</PrimaryLink>

// Button link
<ButtonLink href="/signup">Sign Up</ButtonLink>`}
            </pre>
          </div>
          
          <div>
            <h4 className="font-semibold mb-2">Navigation</h4>
            <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto">
{`import { NavLink, BreadcrumbLink } from '@acutrack-bookprint/acutrack-ds';

// Navigation link
<NavLink href="/dashboard" className="px-3 py-2 rounded">
  Dashboard
</NavLink>

// Breadcrumb
<BreadcrumbLink href="/docs">Documentation</BreadcrumbLink>`}
            </pre>
          </div>
          
          <div>
            <h4 className="font-semibold mb-2">Social Links</h4>
            <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto">
{`import { SocialLink } from '@acutrack-bookprint/acutrack-ds';

// Social media link
<SocialLink href="https://twitter.com/acutrack" className="p-2 rounded-full">
  Twitter
</SocialLink>`}
            </pre>
          </div>
        </div>
      </SubSection>
    </SectionContainer>
  );
};
